import { useEffect, useMemo, useRef, useState, type HTMLInputTypeAttribute, type MutableRefObject } from 'react'
import { Pressable, Text, TextInput, View } from 'react-native'
import { useController, type Control, type FieldPath, type FieldValues, type RegisterOptions } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { isWeb } from '@/constants/app'
import { cn } from '@/utils/cn'
import { Input, type InputProps } from '@/components/base'
import { Cancel } from '@/components/icon'

interface NumberInputProps<TValues extends FieldValues, TPath extends FieldPath<TValues>>
  extends Omit<InputProps, 'defaultValue' | 'error' | 'onChange' | 'value'> {
  label?: string
  labelRightElement?: React.ReactElement
  labelClassName?: string
  hint?: string
  type?: HTMLInputTypeAttribute
  control: Control<TValues>
  name: TPath
  rules?: RegisterOptions<TValues, TPath>
  hasPlaceholder?: boolean
}

const VALID_DECIMAL = 2

export function NumberInput<TValues extends FieldValues, TPath extends FieldPath<TValues>>({
  label,
  labelRightElement,
  labelClassName,
  hint,
  type,
  control,
  name,
  rules,
  hasPlaceholder = true,
  className,
  ...props
}: NumberInputProps<TValues, TPath>) {
  const { t } = useTranslation()
  const [isFocus, setIsFocus] = useState<boolean>(false)
  const [isLateFocus, setIsLateFocus] = useState(false)
  const [isClearBtnFocus, setIsClearBtnFocus] = useState(false)
  const inputRef: MutableRefObject<TextInput | null> = useRef(null)
  const {
    field: { value, ref, onChange },
    fieldState: { error }
  } = useController({ control, name, rules })
  const [displayValue, setDisplayValue] = useState('')
  const hasError = error !== undefined
  const hasClearButton = isWeb ? (isClearBtnFocus || isLateFocus) && displayValue : isFocus && displayValue

  const rightElement = useMemo(() => {
    const Clear = hasClearButton ? (
      <Pressable
        accessibilityLabel={t('ui:clearInput')}
        accessibilityRole={'button'}
        className={cn('size-8 items-center justify-center outline-offset-1', hasError && 'outline-danger')}
        hitSlop={6}
        onPress={() => {
          setDisplayValue('')
          onChange(0)
          setIsClearBtnFocus(false)
          inputRef.current?.focus()
        }}
        onFocus={() => setIsClearBtnFocus(true)}
        onBlur={() => setIsClearBtnFocus(false)}
      >
        <Cancel className={'size-5 text-text-quaternary'} />
      </Pressable>
    ) : undefined

    return Clear
  }, [hasClearButton, onChange, hasError, t])

  useEffect(() => {
    if (isFocus) return
    setDisplayValue(value ? String(value) : '')
  }, [isFocus, value])

  useEffect(() => {
    setIsLateFocus(isFocus)
  }, [isFocus])

  const handleChange = (value: string) => {
    if (value === '') {
      setDisplayValue('')
      onChange(0)
      return
    }
    const stripped = value.replace(/[^\d.]/g, '')
    if (stripped === '') {
      setDisplayValue('')
      onChange(0)
      return
    }
    if (stripped[0] === '.') {
      setDisplayValue('0.')
      onChange(0)
      return
    }
    if (stripped[0] === '0' && stripped.length !== 1 && stripped[1] !== '.') {
      setDisplayValue('0')
      onChange(0)
      return
    }
    const firstDotPos = stripped.indexOf('.')
    const lastDotPos = stripped.lastIndexOf('.')
    if (firstDotPos !== lastDotPos) {
      setDisplayValue(stripped.slice(0, lastDotPos))
      onChange(parseFloat(stripped))
      return
    }
    if (firstDotPos === -1) {
      setDisplayValue(stripped)
      onChange(parseFloat(stripped))
      return
    }
    const displayValue = `${stripped.slice(0, firstDotPos)}.${stripped.slice(firstDotPos + 1, firstDotPos + 1 + VALID_DECIMAL)}`
    setDisplayValue(displayValue)
    onChange(parseFloat(displayValue))
  }

  return (
    <View className={'gap-y-1'}>
      <View className={cn('flex-row items-baseline justify-between', labelClassName)}>
        {label && <Text className={'text-sm font-medium text-text-secondary'}>{label}</Text>}
        {labelRightElement}
      </View>
      <Input
        {...props}
        testID={props.testID ?? `input-${String(name).replaceAll('.', '-')}`}
        accessibilityLabel={props.accessibilityLabel ?? label ?? props.placeholder}
        ref={(e) => {
          ref(e)
          inputRef.current = e
        }}
        value={displayValue}
        onChange={handleChange}
        onFocus={() => setIsFocus(true)}
        onBlur={() => setIsFocus(false)}
        rightElement={rightElement}
        error={hasError}
        className={className}
      />
      {hint && <Text className={'text-xs font-medium text-text-tertiary'}>{hint}</Text>}
      {hasError && <Text className={'text-xs font-medium text-text-danger'}>{error.message}</Text>}
      {hasPlaceholder && !hasError && !hint && <View className={'h-4'} />}
    </View>
  )
}
